import { io, Socket } from 'socket.io-client';
import { getStoredImpersonation } from '@/lib/impersonation';

// ── Socket.io client singleton ──────────────────────────────────────────

let socket: Socket | null = null;
/** How many mounted useSocket() calls are holding the connection open. */
let holders = 0;

const SOCKET_URL = import.meta.env.VITE_API_URL || '';
const SOCKET_PATH = '/ws';

/** A JWT's claims, read WITHOUT verifying it: for deciding what this tab shows, never what it may do. */
function unverifiedClaims(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')), (c) => c.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Whether this tab's session gets the staff live feed — the /ws socket (live updates, the "Live" dot,
 * notification toasts) and /logs (Live Logs). Staff sessions only; the API refuses everyone else:
 *
 *  - A partner's own login: its token carries `partnerId` (and so does its profile). The API refuses
 *    it on both sockets, so trying only produced a permanent "Offline"/"Reconnecting…" indicator.
 *  - A Login-as-Partner tab (the partner session in this tab's sessionStorage). Its requests go out
 *    with the partner token, but a socket would have connected with the staff member's OWN token from
 *    localStorage — streaming tenant-wide staff events (every customer's WhatsApp, every OLT alarm)
 *    into a tab showing what the partner sees. Deliberately none: the tab shows the partner's view,
 *    and partners get no live feed. The staff member's other tabs keep theirs.
 *  - Any other token that is not a staff access token (the same claims the API's tokenKind reads).
 *
 * `token` is the session's access token (the auth store's, which mirrors localStorage).
 */
export function staffRealtimeAllowed(profilePartnerId: string | null | undefined, token: string | null): boolean {
  if (getStoredImpersonation()) return false;
  if (profilePartnerId) return false;
  if (!token) return false;
  const claims = unverifiedClaims(token);
  if (!claims) return false;
  return claims.type === undefined && claims.partnerId == null && claims.imp == null && claims.aud === undefined;
}

export function getSocket(): Socket {
  if (!socket) {
    socket = io(`${SOCKET_URL}${SOCKET_PATH}`, {
      // Read on every connection attempt, not once. The stored token is renewed while the page stays
      // open, and the gateway drops a handshake with an expired token for good (the client does not
      // retry a server-side disconnect) — so a reconnect after an API restart must present the current one.
      // Always in the handshake's auth payload: the API reads no token from the URL.
      auth: (cb) => cb({ token: localStorage.getItem('isp_token') }),
      transports: ['websocket', 'polling'],
      autoConnect: false,
      reconnection: true,
      // Never stop trying. This used to give up after 10 attempts, and giving up is permanent — the
      // client sits there showing "Offline" until someone reloads the page, even once the API is
      // back. An API restart is exactly when a live dashboard must heal itself unattended, so it
      // retries indefinitely with the delay capped at 30s; an idle reconnect attempt every half
      // minute costs nothing next to a dashboard that has silently stopped updating.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
      timeout: 20000,
    });
  }
  return socket;
}

/**
 * Hold the connection open for one consumer (one mounted useSocket), opening it if it is not. Only
 * for a session staffRealtimeAllowed admits.
 *
 * The connection is shared, so it closes when the LAST holder lets go. It used to close whenever any
 * consumer unmounted: leaving a page with live hooks cut the connection the top bar and the
 * notification toasts were still listening on, and they stayed dead until a reload.
 */
export function holdSocket(): Socket {
  holders += 1;
  const s = getSocket();
  if (!s.active) s.connect();
  return s;
}

/** Let go of one hold; the last one closes the connection. */
export function releaseSocket(): void {
  holders = Math.max(0, holders - 1);
  if (holders === 0) disconnectSocket();
}

/** Close it now, whoever holds it (signing out). */
export function disconnectSocket(): void {
  holders = 0;
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

// ── Event type definitions ──────────────────────────────────────────────

export interface WsNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  link?: string;
}

export interface WsDeviceStatus {
  deviceId: string;
  deviceType: 'nas' | 'olt' | 'onu';
  name: string;
  isReachable: boolean;
  /** false ⇒ reachable via management TCP but SNMP agent not answering ("Online – SNMP Unresponsive"). */
  snmpResponsive?: boolean;
  metrics?: Record<string, unknown>;
}

export interface WsTicketUpdate {
  ticketId: string;
  ticketNumber: string;
  action: 'created' | 'updated' | 'assigned' | 'resolved' | 'closed';
  summary: string;
}

export interface WsSubscriberChange {
  subscriberId: string;
  subscriberCode: string;
  action: 'created' | 'updated' | 'activated' | 'suspended' | 'expired';
  name: string;
}

export interface WsBillingEvent {
  type: 'invoice_generated' | 'payment_received' | 'payment_failed';
  subscriberId?: string;
  amount?: number;
  count?: number;
}

export interface WsJobProgress {
  jobId: string;
  jobName: string;
  progress: number;
  status: 'running' | 'completed' | 'failed';
  message?: string;
}

export interface WsDashboardUpdate {
  metric: string;
  value: number;
  previousValue?: number;
}

// ── OLT/ONU real-time event types ─────────────────────────────────────

export interface WsOnuStatusChange {
  oltId: string;
  oltName: string;
  ponPort: number;
  onuIndex: number;
  serialNumber?: string;
  previousStatus: string;
  newStatus: string;
  source: 'polling' | 'trap';
}

export interface WsOnuAlarm {
  oltId: string;
  oltName: string;
  ponPort: number;
  onuIndex: number;
  serialNumber?: string;
  alarmType: 'low_rx_power' | 'high_tx_power' | 'onu_offline' | 'los';
  severity: 'warning' | 'critical';
  rxPower?: number;
  txPower?: number;
  threshold?: number;
}

export interface WsOnuDiscovered {
  oltId: string;
  oltName: string;
  ponPort: number;
  onuIndex: number;
  serialNumber: string;
  status: string;
  macAddress?: string;
}

export interface WsDeviceRecovery {
  deviceId: string;
  deviceType: 'olt';
  name: string;
  isReachable: true;
  downtimeSeconds: number | null;
  previousFailures: number;
  recoveredAt: string;
}

export interface WsOnuSignalUpdate {
  onuId: string;
  serialNumber: string;
  rxPower: number | null;
  txPower: number | null;
  oltRxPower: number | null;
  status: string;
  lastPolledAt: string;
}

// ── Field dispatch ──
export interface WsTechnicianLocation {
  userId: string;
  name?: string;
  lat: number;
  lng: number;
  status?: string;
  at: string;
  battery?: number;
}

export interface WsTechnicianStatus {
  userId: string;
  status: string;
}

export interface WsJobUpdate {
  jobId: string;
  jobNumber: string;
  status: string;
  assignedTo?: string | null;
  action: 'created' | 'assigned' | 'accepted' | 'rejected' | 'status' | 'reassigned' | 'completed' | 'photo';
}

export interface WsMisrouteAlert {
  jobId: string;
  jobNumber: string;
  technicianId: string;
  technicianName?: string;
  reasons: string[];
  severity: string;
  plannedKm: number | null;
  actualKm: number | null;
  extraKm: number | null;
  plannedEtaMin: number | null;
  projectedEtaMin: number | null;
  extraMin: number | null;
  navUrl: string;
  detectedAt: string;
}
